/*  CubicleInvitePreview — /cubicle-invites/:invite_id/preview
 *
 *  Landing page an invitee sees when they tap a push deep-link.
 *  Shows the project card, production house identity, and inviter
 *  details BEFORE they enter the cubicle. Two actions:
 *    · Enter Cubicle   → accepts + navigates to the cubicle
 *    · Decline         → opens a small reason modal
 *
 *  Auth required. 404 for anyone but the specific invitee.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, MapPin, Building2, Film, DoorOpen, X,
  BadgeCheck, Clock, User, Sparkles, Briefcase,
} from "lucide-react";


const CUBICLE_ICON_COLOR = {
  costume:    "#F472B6",
  camera:     "#60A5FA",
  direction:  "#F87171",
  production: "#FBBF24",
  sound:      "#A78BFA",
  light:      "#FCD34D",
  art:        "#34D399",
  editorial:  "#FB923C",
  insurance:  "#22D3EE",
  legal:      "#818CF8",
};


export default function CubicleInvitePreview() {
  const { invite_id } = useParams();
  const nav = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cubicle-invites/${invite_id}/preview`);
      setData(data);
    } catch (e) {
      const detail = formatApiErrorDetail(e?.response?.data?.detail);
      toast.error(detail || "Could not load invite");
      setData(null);
    } finally { setLoading(false); }
  }, [invite_id]);
  useEffect(() => { load(); }, [load]);

  const enter = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/cubicle-invites/${invite_id}/accept`, {});
      toast.success("Welcome to the cubicle. The inviter has been notified.");
      // Deep-link into the cubicle view.
      nav(`/projects/${r.project_id}/cubicles?cubicle=${encodeURIComponent(r.cubicle)}&scope=cubicle_only`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not enter");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/cubicle-invites/${invite_id}/decline`,
        { reason: declineReason });
      toast.success("Invitation declined.");
      setDeclining(false); setDeclineReason("");
      nav("/me");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white/50 flex items-center justify-center text-xs uppercase tracking-[0.28em]">
        Loading invite…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center flex-col gap-4">
        <div className="text-white/60">This invite is not available.</div>
        <Link to="/me" className="text-[10px] uppercase tracking-[0.28em] text-fuchsia-400"
              data-testid="preview-back-to-desk">← Back to My Desk</Link>
      </div>
    );
  }

  const { invite, cubicle, project, org, inviter } = data;
  const accent = CUBICLE_ICON_COLOR[cubicle.key] || "#FF2E63";
  const alreadyIn = invite.invite_status === "accepted" || invite.invite_status === "hired";

  return (
    <div className="min-h-screen bg-neutral-950 text-white/90"
         data-testid="cubicle-invite-preview-page">
      {/* Header strip */}
      <div className="border-b border-white/10 bg-neutral-950/85 backdrop-blur">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/me"
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-white/55 hover:text-white"
                data-testid="preview-back-to-desk">
            <ChevronLeft size={12}/> My Desk
          </Link>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
            Cubicle Preview
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-10 space-y-8">
        {/* Hero card — cubicle + role summary */}
        <div className="rounded-3xl border border-white/10 bg-neutral-900/60 p-8 relative overflow-hidden"
             data-testid="preview-hero">
          <div className="absolute inset-0 pointer-events-none opacity-40"
               style={{background: `radial-gradient(700px 260px at 20% 0%, ${accent}22, transparent 60%)`}}/>
          <div className="relative">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: accent }}>
              {cubicle.label} · Cubicle Invite
            </div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl uppercase tracking-tight leading-[0.95]">
              You&apos;re invited to&nbsp;
              <span style={{ color: accent }}>{invite.role_label || cubicle.label}</span>
            </h1>
            <div className="mt-3 text-sm text-white/60">
              This is a preview. When you enter the cubicle, the inviter is
              notified you&apos;re inside — chat, call, and discuss the role
              before they hit Hire.
            </div>
          </div>
        </div>

        {/* Project + PH */}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-white/10 bg-neutral-900/40 p-6"
               data-testid="preview-project-card">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 flex items-center gap-1.5">
              <Film size={11}/> Project
            </div>
            <div className="mt-2 font-display text-2xl uppercase tracking-tight">
              {project.title || "Untitled"}
            </div>
            <div className="mt-2 text-xs text-white/60 flex flex-wrap gap-x-3 gap-y-1">
              {project.project_type && (
                <span className="uppercase tracking-wider">{project.project_type.replace(/_/g, " ")}</span>
              )}
              {project.status && <span>· {project.status}</span>}
            </div>
            {(project.location || project.start_date) && (
              <div className="mt-3 text-[11px] text-white/60 flex flex-wrap gap-3">
                {project.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={11}/> {project.location}
                  </span>
                )}
                {project.start_date && (
                  <span className="inline-flex items-center gap-1">
                    <Clock size={11}/> {project.start_date}
                  </span>
                )}
              </div>
            )}
          </div>

          {org && (
            <div className="rounded-2xl border border-white/10 bg-neutral-900/40 p-6"
                 data-testid="preview-org-card">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 flex items-center gap-1.5">
                <Building2 size={11}/> Production House
              </div>
              <div className="mt-2 flex items-center gap-3">
                {org.logo_url && (
                  <img src={org.logo_url} alt=""
                       className="w-10 h-10 rounded-lg object-cover"
                       onError={(e) => { e.target.style.display = "none"; }} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl uppercase tracking-tight truncate">
                    {org.brand_name || "—"}
                  </div>
                  {org.verified && (
                    <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-emerald-400 uppercase tracking-[0.22em]">
                      <BadgeCheck size={10}/> Verified
                    </div>
                  )}
                </div>
              </div>
              {org.public_handle && (
                <Link to={`/agency/${org.public_handle}`}
                      className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-fuchsia-400 hover:text-fuchsia-300"
                      data-testid="preview-org-visit">
                  Visit profile →
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Inviter */}
        {inviter && (
          <div className="rounded-2xl border border-white/10 bg-neutral-900/40 p-6"
               data-testid="preview-inviter-card">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 flex items-center gap-1.5">
              <User size={11}/> Invited by
            </div>
            <div className="mt-3 flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-white/8 flex items-center justify-center overflow-hidden">
                {inviter.avatar_url ? (
                  <img src={inviter.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display text-2xl text-white/70">
                    {(inviter.name || "?").slice(0, 1)}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl uppercase tracking-tight truncate">
                  {inviter.name || "Team member"}
                </div>
                <div className="mt-1 text-xs text-white/60 flex flex-wrap gap-2">
                  {inviter.headline && <span>{inviter.headline}</span>}
                  {inviter.role_at_org && <span>· {inviter.role_at_org}</span>}
                  {inviter.primary_role && (
                    <span className="inline-flex items-center gap-1">
                      <Briefcase size={11}/> {inviter.primary_role}
                    </span>
                  )}
                </div>
              </div>
              {inviter.public_handle && (
                <Link to={`/u/${inviter.public_handle}`}
                      className="text-[10px] uppercase tracking-[0.22em] text-fuchsia-400 hover:text-fuchsia-300 shrink-0"
                      data-testid="preview-inviter-visit">
                  View profile →
                </Link>
              )}
            </div>
          </div>
        )}

        {/* CTAs */}
        <div className="rounded-2xl border p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between"
             style={{ borderColor: `${accent}44`, background: `${accent}0a` }}
             data-testid="preview-cta-panel">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: accent }}>
              Ready to talk?
            </div>
            <div className="mt-1 font-display text-xl uppercase tracking-tight">
              {alreadyIn
                ? "You're already in this cubicle"
                : "Open the cubicle to start the conversation"}
            </div>
            <p className="mt-2 text-xs text-white/60 max-w-md">
              Entering means the discussion is open — you can chat, call,
              share references. The producer decides when to hit
              &quot;Hired&quot;.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            {!alreadyIn && (
              <Button variant="ghost"
                      className="rounded-full text-white/70 hover:text-white"
                      onClick={() => setDeclining(true)}
                      disabled={busy}
                      data-testid="preview-decline-btn">
                <X size={14}/> Decline
              </Button>
            )}
            <Button onClick={enter}
                    disabled={busy}
                    className="rounded-full px-6 h-11 uppercase tracking-[0.2em] text-[11px]"
                    style={{ background: accent, color: "#0a0812" }}
                    data-testid="preview-enter-btn">
              <DoorOpen size={14}/> {alreadyIn ? "Open cubicle" : "Enter cubicle"}
            </Button>
          </div>
        </div>
      </div>

      {/* Decline modal */}
      {declining && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setDeclining(false)}
             data-testid="preview-decline-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="bg-neutral-950 border border-white/15 rounded-2xl p-6 max-w-md w-full">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
              Decline · {cubicle.label}
            </div>
            <div className="font-display text-2xl mt-2 uppercase tracking-tight">
              {project.title}
            </div>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Reason (optional — helps the PH schedule around you)…"
              className="mt-4 w-full h-24 bg-black/50 border border-white/15 rounded-lg p-3 text-sm text-white/85"
              data-testid="preview-decline-reason"/>
            <div className="mt-4 flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setDeclining(false)}
                      className="text-white/60">Cancel</Button>
              <Button onClick={decline} disabled={busy}
                      className="bg-red-500/80 hover:bg-red-500 text-white rounded-full px-6"
                      data-testid="preview-decline-confirm">
                Decline invitation
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
